<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import AiMarkupAPI from 'dashboard/api/aiMarkup';
import {
  SENTIMENT_STYLES,
  normalizeSentiment,
} from 'dashboard/components-next/Conversation/sentiment';

const props = defineProps({
  // Autotag config, used to colour the returned tags.
  tags: { type: Array, default: () => [] },
});

const { t } = useI18n();

const text = ref('');
const result = ref(null);
const error = ref('');
const isLoading = ref(false);

const EXAMPLES = ['NEGATIVE', 'POSITIVE', 'SPAM'];

const sentimentStyle = computed(
  () => SENTIMENT_STYLES[normalizeSentiment(result.value?.sentiment)]
);

const resultTags = computed(() =>
  (result.value?.tags || []).map(tag => ({
    title: tag,
    color: props.tags.find(item => item.label === tag)?.color || '#64748B',
  }))
);

const rules = computed(() =>
  (result.value?.rules_matched || []).map(rule =>
    typeof rule === 'string' ? rule : rule.name || rule.id
  )
);

const score = computed(() => {
  const value = Number(result.value?.score);
  if (Number.isNaN(value) || result.value?.score === undefined) return '';
  return `${value > 0 ? '+' : ''}${value.toFixed(2)}`;
});

const run = async () => {
  if (!text.value.trim()) return;
  isLoading.value = true;
  error.value = '';
  try {
    const { data } = await AiMarkupAPI.test(text.value.trim());
    result.value = data;
  } catch (e) {
    result.value = null;
    error.value = t('AI_MARKUP.TEST.ERROR');
  } finally {
    isLoading.value = false;
  }
};

const useExample = key => {
  text.value = t(`AI_MARKUP.TEST.EXAMPLES.${key}`);
  result.value = null;
};
</script>

<template>
  <div class="grid gap-4 lg:grid-cols-2">
    <div class="flex flex-col gap-2">
      <textarea
        v-model="text"
        rows="6"
        class="w-full !mb-0 px-3 py-2 text-sm leading-5 rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 resize-y focus:outline-none focus:ring-1 focus:ring-n-brand"
        :placeholder="t('AI_MARKUP.TEST.PLACEHOLDER')"
        @keydown.meta.enter.prevent="run"
        @keydown.ctrl.enter.prevent="run"
      />
      <div class="flex flex-wrap items-center gap-2">
        <span class="text-xs text-n-slate-10">{{
          t('AI_MARKUP.TEST.TRY')
        }}</span>
        <button
          v-for="key in EXAMPLES"
          :key="key"
          type="button"
          class="h-6 px-2 text-xs rounded-md bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 hover:text-n-slate-12"
          @click="useExample(key)"
        >
          {{ t(`AI_MARKUP.TEST.EXAMPLE_LABELS.${key}`) }}
        </button>
        <span class="flex-1" />
        <Button
          :label="t('AI_MARKUP.TEST.RUN')"
          icon="i-ri-sparkling-2-line"
          size="sm"
          :is-loading="isLoading"
          :disabled="!text.trim() || isLoading"
          @click="run"
        />
      </div>
    </div>

    <div
      class="flex flex-col gap-3 p-4 border rounded-lg min-h-[10rem]"
      :class="
        result ? 'border-n-weak bg-n-alpha-1' : 'border-dashed border-n-weak'
      "
    >
      <p
        v-if="error"
        class="flex items-center gap-2 m-auto text-sm text-n-ruby-11"
      >
        <span class="i-lucide-circle-alert size-4" />
        {{ error }}
      </p>
      <p
        v-else-if="!result"
        class="flex flex-col items-center gap-2 m-auto text-sm text-center text-n-slate-10"
      >
        <span class="i-ri-sparkling-2-line size-6 text-n-iris-9" />
        {{ t('AI_MARKUP.TEST.EMPTY') }}
      </p>
      <template v-else>
        <div class="flex flex-wrap items-center gap-2">
          <span
            v-if="sentimentStyle"
            class="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-sm font-semibold"
            :class="sentimentStyle.badgeClass"
          >
            <span class="size-4" :class="sentimentStyle.icon" />
            {{ t(sentimentStyle.label) }}
            <span v-if="score" class="font-mono text-xs opacity-80">{{
              score
            }}</span>
          </span>
          <span
            class="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-xs font-semibold"
            :class="
              result.spam
                ? 'bg-n-ruby-3 text-n-ruby-11'
                : 'bg-n-teal-3 text-n-teal-11'
            "
          >
            <span
              class="size-3.5"
              :class="
                result.spam ? 'i-lucide-shield-alert' : 'i-lucide-shield-check'
              "
            />
            {{
              result.spam
                ? t('AI_MARKUP.TEST.SPAM')
                : t('AI_MARKUP.TEST.NOT_SPAM')
            }}
          </span>
          <span class="flex-1" />
          <span
            v-if="result.latency_ms"
            class="inline-flex items-center gap-1 text-xs text-n-slate-10"
          >
            <span class="i-lucide-zap size-3.5" />
            {{ t('AI_MARKUP.TEST.LATENCY', { ms: result.latency_ms }) }}
          </span>
        </div>
        <div v-if="resultTags.length" class="flex flex-wrap gap-1">
          <span
            v-for="tag in resultTags"
            :key="tag.title"
            class="inline-flex items-center gap-1.5 h-6 px-2 rounded-md border border-n-weak bg-n-solid-1 text-xs text-n-slate-12"
          >
            <span
              class="rounded-full size-2"
              :style="{ background: tag.color }"
            />
            {{ tag.title }}
          </span>
        </div>
        <p v-if="result.summary" class="m-0 text-sm leading-5 text-n-slate-12">
          {{ result.summary }}
        </p>
        <p v-if="result.spam_reason" class="m-0 text-xs text-n-ruby-11">
          {{ result.spam_reason }}
        </p>
        <div v-if="rules.length" class="flex flex-wrap items-center gap-1">
          <span class="text-xs text-n-slate-10">{{
            t('AI_MARKUP.TEST.RULES')
          }}</span>
          <span
            v-for="rule in rules"
            :key="rule"
            class="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-n-amber-3 text-n-amber-11 text-xs font-medium"
          >
            <span class="i-lucide-shield size-3" />
            {{ rule }}
          </span>
        </div>
        <span
          v-if="result.model"
          class="mt-auto font-mono text-[11px] text-n-slate-10"
        >
          {{ result.model }}
        </span>
      </template>
    </div>
  </div>
</template>
